import React from "react";
import clsx from "clsx";
import AutoSizer from "react-virtualized-auto-sizer";

import useMediaQuery from "@material-ui/core/useMediaQuery";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Box from "@material-ui/core/Box";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import useTheme from "@material-ui/core/styles/useTheme";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AbcCircle from "components/AbcCircle";
import AppPageTable from "components/AppPageTable";
import AppPageTableHeader from "components/AppPageTableHeader";
import AppPageTableContent from "components/AppPageTableContent";
import DaysOffDiagram from "components/DaysOffDiagram";
import RiskFactorScoring from "components/RiskFactorScoring";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import VirtualList from "components/VirtualList";
import { useRacehorse360Api } from "hooks/api";
import { differenceInDaysOrNA } from "utils/date-utils";
import { getPersonFullName } from "utils/person";
import { checkSortingType } from "utils/sort-utils";
import { getTrainerFieldContentOrNumber } from "utils/trainer";
import {
  SortOrder,
  SortOrderExtended,
  SortOrderType
} from "interfaces/SortOrder";
import HorseListRow from "./HorseListRow";

import useStyles from "./styles";

export enum SortByFields {
  NAME = "name",
  TRAINER_FIRSTNAME = "trainer.firstName",
  TRAINER_LASTNAME = "trainer.lastName",
  HEALTH_WATCH_STATUS = "healthWatchStatus",
  HIGH_SPEED_FURLONGS = "highSpeedFurlongs",
  LAST_RACE_DATE = "lastRaceDate",
  LAST_WORK_DATE = "lastWorkDate",
  RISK_FACTORS = "riskFactorsCount",
  RISK_FACTOR_SCORE = "riskFactorScore"
}

export interface ICell {
  id: string;
  label: string;
  type?: racehorse360.HorseDetailPageDataColumn;
  orderBy: string[];
  align?: "left" | "right" | "center";
  className?: string;
  desktopOnly?: boolean;
  additionalClassName?: string;
  render: (isXlScreen?: boolean) => React.ReactNode;
}

export interface Props {
  onSortClick: (orderBy, order) => void;
  horses: racehorse360.IHorse[];
  order: SortOrderType;
  orderBy: string[];
  tableBodyRef: React.RefObject<HTMLDivElement>;
}

const HorseList = React.memo((props: Props) => {
  const { onSortClick, horses, order, orderBy, tableBodyRef } = props;

  const classes = useStyles();

  const theme = useTheme();
  const matchesDownSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });
  const { useGetUser } = useRacehorse360Api();
  const { currentUser } = useLoggedInUser();
  const isRegulatoryVet = currentUser.isRegulatoryVet;

  const { data } = useGetUser(
    `${currentUser.rh360Id}-user-settings`,
    {
      id: currentUser.rh360Id,
      getOptions: {
        select: ["preferences.horseDetailPageDataColumn"]
      }
    },
    {
      onError: error => {
        console.error(error);
      },
      enabled: matchesDownSm
    }
  );

  const horseDetailPageDataColumn =
    data?.preferences?.horseDetailPageDataColumn;

  const shouldShowAdditionalHeaderCell = [
    matchesDownSm,
    horseDetailPageDataColumn
  ].every(Boolean);

  const getCells = (horse: racehorse360.IHorse): ICell[] => {
    return [
      {
        id: "name",
        label: "Horse",
        orderBy: [SortByFields.NAME],
        additionalClassName: classes.horseName,
        render: () => <span>{horse.name}</span>
      },
      {
        id: "trainer.firstName",
        label: "Trainer",
        orderBy: [
          SortByFields.TRAINER_LASTNAME,
          SortByFields.TRAINER_FIRSTNAME
        ],
        render: () => (
          <>
            <span className={classes.trainerName}>
              {getTrainerFieldContentOrNumber(
                getPersonFullName(horse.trainer, "%L %M %F"),
                horse.trainer
              )}
            </span>
            <span className={classes.trainerNameShort}>
              {getTrainerFieldContentOrNumber(
                getPersonFullName(horse.trainer, "%L %m %f"),
                horse.trainer
              )}
            </span>
          </>
        )
      },
      {
        id: "healthWatchStatus",
        label: "Risk Level",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_RISK_LEVEL,
        orderBy: [SortByFields.HEALTH_WATCH_STATUS],
        additionalClassName: classes.centerAligning,
        desktopOnly: true,
        render: () => <AbcCircle healthWatchStatus={horse.healthWatchStatus} />
      },
      {
        id: "highSpeedFurlongs",
        label: "HSF",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_HIGH_SPEED_FURLONGS,
        orderBy: [SortByFields.HIGH_SPEED_FURLONGS],
        additionalClassName: classes.centerAligning,
        desktopOnly: true,
        render: () => <>{horse.highSpeedFurlongs}</>
      },
      {
        id: "riskFactorScore",
        label: matchesDownSm ? "Risk Score" : "Risk Factor Score",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_RISK_FACTOR_SCORE,
        orderBy: [SortByFields.RISK_FACTOR_SCORE],
        className: classes.tableCellLink,
        additionalClassName: classes.centerAligning,
        desktopOnly: true,
        render: () =>
          Boolean(horse.riskFactorScore) && (
            <RiskFactorScoring value={horse.riskFactorScore} />
          )
      },
      {
        id: "riskFactorsCount",
        label: "Risk Factors",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_RISK_FACTORS,
        orderBy: [SortByFields.RISK_FACTORS],
        additionalClassName: classes.centerAligning,
        desktopOnly: true,
        render: () => <>{horse.riskFactorsCount}</>
      },
      {
        id: "lastRaceDate",
        label: matchesDownSm ? "DLR" : "Days Last Race",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_DAYS_LAST_RACE,
        orderBy: [SortByFields.LAST_RACE_DATE],
        additionalClassName: classes.centerAligning,
        desktopOnly: true,
        render: () => <>{differenceInDaysOrNA(horse.lastRaceDate)}</>
      },
      {
        id: "lastWorkDate",
        label: matchesDownSm ? "DLW" : "Days Last Work",
        type: racehorse360.HorseDetailPageDataColumn
          .HORSE_DETAIL_PAGE_DATA_COLUMN_DAYS_LAST_WORK,
        orderBy: [SortByFields.LAST_WORK_DATE],
        additionalClassName: classes.daysOffColumn,
        desktopOnly: true,
        render: isLineDiagram => (
          <DaysOffDiagram
            horse={horse}
            className={classes.daysOffDiagram}
            type={isLineDiagram ? "line" : "donut"}
            simplified
          />
        )
      }
    ];
  };

  const createSortHandler =
    (orderBy: string[]) => (event: React.MouseEvent<unknown>) => {
      handleSortClick(event, orderBy);
    };

  const handleSortClick = (
    event: React.MouseEvent<unknown>,
    newOrderBy: string[]
  ) => {
    const { isOneOfAsc } = checkSortingType(order);
    const isAscDirection =
      orderBy.toString() === newOrderBy.toString() && isOneOfAsc;
    let newOrder: SortOrderType;

    switch (newOrderBy.toString()) {
      case SortByFields.HEALTH_WATCH_STATUS:
        newOrder = isAscDirection
          ? SortOrderExtended.DESC_NULLS_LAST
          : SortOrderExtended.ASC_NULLS_FIRST;
        break;
      case SortByFields.LAST_WORK_DATE:
        newOrder = isAscDirection
          ? SortOrderExtended.DESC_NULLS_FIRST
          : SortOrderExtended.ASC_NULLS_LAST;
        break;
      case SortByFields.LAST_RACE_DATE:
        newOrder = isAscDirection
          ? SortOrderExtended.DESC_NULLS_FIRST
          : SortOrderExtended.ASC_NULLS_LAST;
        break;
      default:
        newOrder = isAscDirection ? SortOrder.DESC : SortOrder.ASC;
    }

    onSortClick(newOrderBy, newOrder);
  };

  const renderHeadCell = (headCell, forceDisplay = false) => {
    if (!headCell) {
      return <></>;
    }
    const isActive = orderBy.includes(headCell.id);
    let isActiveAsc: boolean;
    let isActiveDesc: boolean;

    if (isActive) {
      const orderBy = headCell.orderBy[0];

      switch (orderBy) {
        case SortByFields.LAST_WORK_DATE:
          isActiveAsc = order === SortOrderExtended.DESC_NULLS_FIRST;
          isActiveDesc = order === SortOrderExtended.ASC_NULLS_LAST;
          break;
        case SortByFields.LAST_RACE_DATE:
          isActiveAsc = order === SortOrderExtended.DESC_NULLS_FIRST;
          isActiveDesc = order === SortOrderExtended.ASC_NULLS_LAST;
          break;
        case SortByFields.HEALTH_WATCH_STATUS:
          isActiveAsc = order === SortOrderExtended.ASC_NULLS_FIRST;
          isActiveDesc = order === SortOrderExtended.DESC_NULLS_LAST;
          break;
        default:
          isActiveAsc = order === SortOrder.ASC;
          isActiveDesc = order === SortOrder.DESC;
      }
    }

    return (
      <Box
        className={clsx(classes.tableHeaderCell, {
          [classes.hideOnTablet]: headCell.desktopOnly && !forceDisplay
        })}
        key={headCell.id}
      >
        <TableSortLabel
          active={isActive}
          onClick={createSortHandler(headCell.orderBy)}
          hideSortIcon
        >
          {headCell.label}
          <Box className={classes.icons}>
            <Box
              className={clsx(classes.iconUp, {
                [classes.iconSort]: isActiveAsc
              })}
            >
              <ArrowDropUpIcon />
            </Box>
            <Box
              className={clsx(classes.iconDown, {
                [classes.iconSort]: isActiveDesc
              })}
            >
              <ArrowDropDownIcon />
            </Box>
          </Box>
        </TableSortLabel>
      </Box>
    );
  };

  const renderCells = () => {
    return getCells({}).map(cell => renderHeadCell(cell));
  };

  const renderRows = () =>
    horses.map((horse, index) => (
      <HorseListRow
        key={horse.id}
        className={index % 2 ? classes.rowOdd : classes.rowEven}
        horse={horse}
        getCells={getCells}
        horseDetailPageDataColumn={horseDetailPageDataColumn}
      />
    ));

  return (
    <AppPageTable>
      <AppPageTableHeader
        className={clsx(classes.appPageTableHeader, {
          [classes.customizedForMobile]: Boolean(horseDetailPageDataColumn),
          regulatoryVetView: isRegulatoryVet
        })}
        aria-label="enhanced table"
        aria-labelledby="tableTitle"
      >
        {renderCells()}
        {shouldShowAdditionalHeaderCell &&
          renderHeadCell(
            getCells({}).find(
              c => String(c.type) === String(horseDetailPageDataColumn)
            ),
            true
          )}
      </AppPageTableHeader>

      <AppPageTableContent>
        <AutoSizer>
          {({ height, width }) => (
            <VirtualList
              className={classes.listVirtualized}
              itemClassName={classes.listVirtualizedItem}
              count={horses.length}
              itemHeight={55}
              height={height}
              width={width}
              scrollContainerRef={tableBodyRef}
              itemsBeyond={4}
            >
              {renderRows()}
            </VirtualList>
          )}
        </AutoSizer>
      </AppPageTableContent>
    </AppPageTable>
  );
});

export default HorseList;
